import { createContext, useContext, useState, useEffect, useCallback, useRef, type ReactNode } from 'react';
import type { PublicClientApplication, AccountInfo } from '@azure/msal-browser';
import * as Sentry from '@sentry/react';
import type { AuthConfig } from './msalConfig';

interface AuthContextType {
  isAuthenticated: boolean;
  isLoading: boolean;
  authEnabled: boolean;
  user: AccountInfo | null;
  login: () => void;
  logout: () => void;
  getAccessToken: () => Promise<string | null>;
}

const AuthContext = createContext<AuthContextType>({
  isAuthenticated: false,
  isLoading: true,
  authEnabled: false,
  user: null,
  login: () => {},
  logout: () => {},
  getAccessToken: async () => null,
});

// eslint-disable-next-line react-refresh/only-export-components
export const useAuth = () => useContext(AuthContext);

const LOGIN_SCOPES = ['openid', 'profile', 'email'];
const REFRESH_BUFFER_MS = 5 * 60 * 1000;

function parseJwtExp(token: string): number | null {
  try {
    const payload = token.split('.')[1];
    const decoded = JSON.parse(atob(payload));
    return typeof decoded.exp === 'number' ? decoded.exp : null;
  } catch {
    return null;
  }
}

interface AuthProviderProps {
  msalInstance: PublicClientApplication | null;
  config: AuthConfig;
  children: ReactNode;
}

export function AuthProvider({ msalInstance, config, children }: AuthProviderProps) {
  const [isLoading, setIsLoading] = useState(config.enabled);
  const [user, setUser] = useState<AccountInfo | null>(null);
  const refreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Expiry of the token we last saw. Null means we have not looked yet, which
  // is treated as "unknown", never as "fine".
  const tokenExpRef = useRef<number | null>(null);
  // Whether this tab has been hidden since we last checked the token. A timer
  // is not enough on its own: browsers throttle timers in background tabs and a
  // sleeping laptop suspends them outright, so the scheduled refresh below
  // frequently never runs and the session is dead by the time the user returns.
  const wasHiddenRef = useRef(false);
  // One interactive redirect, however many calls fail at once. A dashboard can
  // fire a dozen requests together and they would otherwise each try to
  // navigate the page.
  const redirectingRef = useRef(false);

  useEffect(() => {
    if (!msalInstance || !config.enabled) {
      setIsLoading(false);
      return;
    }
    const accounts = msalInstance.getAllAccounts();
    if (accounts.length > 0) {
      msalInstance.setActiveAccount(accounts[0]);
      setUser(accounts[0]);
    }
    setIsLoading(false);
  }, [msalInstance, config.enabled]);

  const scheduleRefresh = useCallback((idToken: string) => {
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }
    const exp = parseJwtExp(idToken);
    tokenExpRef.current = exp;
    if (!exp) return;
    const delay = exp * 1000 - REFRESH_BUFFER_MS - Date.now();
    if (delay <= 0) {
      silentRefresh();
      return;
    }
    refreshTimerRef.current = setTimeout(() => {
      refreshTimerRef.current = null;
      silentRefresh();
    }, delay);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [msalInstance, config.enabled]);

  const silentRefresh = useCallback(async () => {
    if (!msalInstance || !config.enabled) return;
    const account = msalInstance.getActiveAccount();
    if (!account) return;
    try {
      const result = await msalInstance.acquireTokenSilent({
        scopes: LOGIN_SCOPES,
        account,
        forceRefresh: true,
      });
      if (result.idToken) scheduleRefresh(result.idToken);
    } catch (err) {
      console.warn('Proactive token refresh failed:', err);
      Sentry.captureException(err, { tags: { source: 'token_refresh' } });
    }
  }, [msalInstance, config.enabled, scheduleRefresh]);

  useEffect(() => {
    return () => {
      if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
    };
  }, []);

  // Returning to the tab is its own refresh trigger.
  //
  // Only after the tab has actually been hidden, so flipping between windows
  // does not hammer the token service; and only when we cannot show the token
  // is still healthy. An unknown expiry counts as not healthy -- the cost of an
  // unnecessary silent refresh is one request, and the cost of skipping a
  // needed one is the user being logged out with no way back.
  useEffect(() => {
    if (!msalInstance || !config.enabled) return;
    const onVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        wasHiddenRef.current = true;
        return;
      }
      if (!wasHiddenRef.current) return;
      wasHiddenRef.current = false;
      const exp = tokenExpRef.current;
      const stillGood = exp !== null && exp * 1000 - REFRESH_BUFFER_MS > Date.now();
      if (stillGood) return;
      silentRefresh();
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('focus', onVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('focus', onVisibilityChange);
    };
  }, [msalInstance, config.enabled, silentRefresh]);

  const login = useCallback(() => {
    if (!msalInstance) return;
    msalInstance.loginRedirect({ scopes: LOGIN_SCOPES });
  }, [msalInstance]);

  const logout = useCallback(() => {
    if (!msalInstance) return;
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }
    msalInstance.logoutRedirect({ postLogoutRedirectUri: window.location.origin });
  }, [msalInstance]);

  const getAccessToken = useCallback(async (): Promise<string | null> => {
    if (!msalInstance || !config.enabled) return null;
    const account = msalInstance.getActiveAccount();
    if (!account) return null;
    for (let attempt = 0; attempt < 3; attempt++) {
      try {
        const result = await msalInstance.acquireTokenSilent({
          scopes: LOGIN_SCOPES,
          account,
          forceRefresh: attempt > 0,
        });
        if (result.idToken) scheduleRefresh(result.idToken);
        return result.idToken;
      } catch {
        if (attempt < 2) await new Promise(r => setTimeout(r, (attempt + 1) * 200));
      }
    }
    // Silent acquisition is exhausted: the refresh token is gone or Azure wants
    // the user to interact. Returning null here was the reported bug -- the app
    // carried on with no token, every call 401'd, and the only way out was
    // logging out and back in by hand. Hand off to MSAL instead.
    if (!redirectingRef.current) {
      redirectingRef.current = true;
      try {
        await msalInstance.acquireTokenRedirect({ scopes: LOGIN_SCOPES, account });
      } catch (err) {
        // The redirect itself failed, so nothing is navigating away and a later
        // call should be allowed to try again.
        redirectingRef.current = false;
        console.warn('Interactive token acquisition failed:', err);
        Sentry.captureException(err, { tags: { source: 'token_interactive' } });
      }
    }
    return null;
  }, [msalInstance, config.enabled, scheduleRefresh]);

  const isAuthenticated = config.enabled ? !!user : true;

  return (
    <AuthContext.Provider value={{
      isAuthenticated,
      isLoading,
      authEnabled: config.enabled,
      user,
      login,
      logout,
      getAccessToken,
    }}>
      {children}
    </AuthContext.Provider>
  );
}
